import React, { useEffect, useState } from "react";
import { useLocalStorage } from "sprint-common-web";

import { ExecutionType } from "../../../../common/types/ExecutionType";
import { OrderStatus } from "../../../common/types/OrderStatus";
import { useOrdersModuleApi } from "../../../common/useOrdersModuleApi";
import { getDefaultOrderListingConfig, OrderListingConfig } from "../config/OrderListingConfig";
import { OrderListingFilters } from "../hooks/useOrderListingFilters";
import { mapToOrderListingItem, OrderListingItem } from "../OrderListingItem";
import { OrderListingContext } from "./OrderListingContext";

export type OrderListingProviderProps = React.PropsWithChildren<unknown>;

const ORDER_LISTING_CONFIG_KEY = "orderListingConfig";
const PINNED_ORDERS_KEY = "pinnedOrders";

export const OrderListingProvider: React.FC<OrderListingProviderProps> = (props: OrderListingProviderProps) => {
    const { getOrderList, getExecutions } = useOrdersModuleApi();

    const [items, setItems] = useState<OrderListingItem[]>([]);
    const [selectedItems, setSelectedItems] = useState<OrderListingItem[]>([]);
    const [mergeListItems, setMergeListItems] = useState<OrderListingItem[]>([]);

    const [listingConfig, setListingConfig] = useLocalStorage<OrderListingConfig>(
        ORDER_LISTING_CONFIG_KEY,
        getDefaultOrderListingConfig()
    );

    const [totalItems, setTotalItems] = useState<number>(0);

    const [pinnedItems, setPinnedItems] = useLocalStorage<string[]>(PINNED_ORDERS_KEY, []);

    useEffect(() => {
        setItems((prevItems) => {
            const pinned: OrderListingItem[] = [];
            const unpinned: OrderListingItem[] = [];

            prevItems.forEach((item) => {
                if (pinnedItems.includes(item.id)) {
                    pinned.push({ ...item, isPinned: true });
                } else {
                    unpinned.push({ ...item, isPinned: false });
                }
            });

            return [...pinned, ...unpinned];
        });
    }, [pinnedItems.length]);

    const reloadData = async (filters: OrderListingFilters): Promise<void> => {
        const response = await getOrderList({
            page: filters.page,
            pageSize: filters.pageSize,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
            statuses: filters.statuses,
            content: filters.content,
            pONumber: filters.pONumber,
            distributorId: filters.distributorId,
            airlineIds: filters.airlineIds,
            systemIds: filters.systemIds,
            filename: filters.filename,
            sources: filters.sources,
            progresses: filters.progresses,
            dueDateFrom: filters.dueDateFrom?.toISOString(),
            dueDateTo: filters.dueDateTo?.toISOString(),
            startDateFrom: filters.startDateFrom?.toISOString(),
            startDateTo: filters.startDateTo?.toISOString(),
            orderIds: pinnedItems
        });

        const listingItems = response.items.map((item) => mapToOrderListingItem(item, pinnedItems));

        setItems(listingItems);
        setTotalItems(response.totalCount);

        lazyLoadExecutions(listingItems);
    };

    const addItemToMergeList = (item: OrderListingItem) => {
        if (!mergeListItems.some((i) => i.id === item.id)) {
            setMergeListItems((prevState) => [...prevState, item]);
        }
    };

    const removeItemsFromMergeList = (itemsIds: string[]) => {
        setMergeListItems((prevState) => prevState.filter((o: OrderListingItem) => !itemsIds.includes(o.id)));
    };

    const lazyLoadExecutions = async (listingItems: OrderListingItem[]) => {
        if (!listingItems.length) {
            return;
        }

        const ids = listingItems.map((item) => item.id);

        const processingExecutionsResponse = await getExecutions(ExecutionType.OrderProcessing, ids);
        const deliveryExecutionsResponse = await getExecutions(ExecutionType.OrderDelivery, ids);

        setItems((prevItems) =>
            prevItems.map((prevItem) => {
                const processingExecution = processingExecutionsResponse.items.find(
                    (item) => item.entityId === prevItem.id
                );

                const deliveryExecution = deliveryExecutionsResponse.items.find(
                    (item) => item.entityId === prevItem.id
                );

                prevItem.processingExecutionArn = processingExecution?.executionArn;
                prevItem.deliveryExecutionArn = deliveryExecution?.executionArn;

                return prevItem;
            })
        );
    };

    const approveDelivery = (orderId: string): void => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === orderId ? { ...item, isModified: true, status: OrderStatus.DeliveryApproved } : item
            )
        );
    };

    const reject = (orderId: string, rejectReason?: string): void => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === orderId
                    ? { ...item, isModified: true, status: OrderStatus.Rejected, statusDetails: rejectReason }
                    : item
            )
        );
    };

    const completeRejected = (orderId: string, completionDetails?: string): void => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === orderId
                    ? {
                          ...item,
                          isModified: true,
                          status: OrderStatus.DeliveryCompleted,
                          statusDetails: completionDetails
                      }
                    : item
            )
        );
    };

    const approveDeliveries = (orderIds: string[]): void => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                orderIds.includes(item.id) ? { ...item, isModified: true, status: OrderStatus.DeliveryApproved } : item
            )
        );
    };

    const deleteOrder = (orderId: string) => {
        setItems((prevState) => prevState.filter((order: OrderListingItem) => order.id !== orderId));
    };

    const updateDeliveryFolder = (orderId: string, deliveryFolder: string): void => {
        setItems((prevItems) =>
            prevItems.map((item) => (item.id === orderId ? { ...item, isModified: true, deliveryFolder } : item))
        );
    };

    const updateOrder = (order: Partial<OrderListingItem> & Required<Pick<OrderListingItem, "id">>): void => {
        setItems((prevItems) => prevItems.map((item) => (item.id === order.id ? { ...item, ...order } : item)));
    };

    const togglePinnedOrder = (orderId: string) => {
        setPinnedItems((prevState) =>
            prevState.includes(orderId) ? prevState.filter((item) => item !== orderId) : [...prevState, orderId]
        );
    };

    return (
        <OrderListingContext.Provider
            value={{
                items,
                listingConfig,
                selectedItems,
                totalItems,
                pinnedItems,
                setSelectedItems,
                reloadData,
                mergeListItems,
                addItemToMergeList,
                removeItemsFromMergeList,
                approveDelivery,
                approveDeliveries,
                saveListingConfig: setListingConfig,
                reject,
                deleteOrder,
                updateDeliveryFolder,
                completeRejected,
                updateOrder,
                togglePinnedOrder
            }}
        >
            {props.children}
        </OrderListingContext.Provider>
    );
};
